// frontend/src/pages/Pipeline/OpportunityDetailPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, PencilIcon, MicrophoneIcon, StopIcon, EnvelopeIcon, PhoneIcon, CalendarIcon, DevicePhoneMobileIcon, PlusIcon } from '@heroicons/react/24/outline';
import useAudioRecorder from '../../hooks/useAudioRecorder';
import api from '../../services/api';
import toast from 'react-hot-toast';
import OpportunityForm from '../../components/Opportunities/OpportunityForm';
import CloseOpportunityModal from '../../components/Opportunities/CloseOpportunityModal';
import AIEnrichmentPanel from '../../components/AI/AIEnrichmentPanel';
import OpportunityTimeline from '../../components/Pipeline/OpportunityTimeline';
import ActivityForm from '../../components/Activities/ActivityForm';
import WhatsappCockpit from '../../components/Pipeline/WhatsappCockpit';
import NbaCard from '../../components/Pipeline/NbaCard';
import { getStagePlaybook } from '../../components/Pipeline/stagePlaybook';
import EmailComposeModal from '../../components/Email/EmailComposeModal';
import ScheduleMeetingModal from '../../components/Calendar/ScheduleMeetingModal';

const STAGE_LABELS = {
  lead: 'Lead', qualified: 'Qualificado', proposal: 'Proposta',
  negotiation: 'Negociação', closed_won: 'Fechou Ganho', closed_lost: 'Fechou Perdido',
};

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(v || 0);

const formatDate = (d) =>
  !d ? 'Sem data' : new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

const OpportunityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [opportunity, setOpportunity] = useState(null);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [closeOutcome, setCloseOutcome] = useState(null); // 'won' | 'lost' | null
  const [description, setDescription] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const [savingDesc, setSavingDesc] = useState(false);
  const [activityModal, setActivityModal] = useState({ open: false, type: null });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const recorder = useAudioRecorder();

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/opportunities/${id}`);
      if (!res.data.success) { setError('Oportunidade não encontrada'); return; }
      const opp = res.data.data;
      setOpportunity(opp);
      setDescription(opp.description || '');
      if (opp.contact_id) {
        try {
          const c = await api.get(`/contacts/${opp.contact_id}`);
          if (c.data.success) setContact(c.data.data);
        } catch (e) { console.error('Erro ao carregar contato:', e); }
      }
    } catch (e) {
      console.error('Erro ao carregar oportunidade:', e);
      setError('Erro ao carregar a oportunidade');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (id) fetchAll(); }, [id, fetchAll]);

  const handleMicClick = async () => {
    try {
      if (!recorder.recording) {
        await recorder.start();
        return;
      }
      const audio = await recorder.stop();
      if (!audio) return;
      setTranscribing(true);
      // timeout largo: notas de hasta ~10 min pueden tardar en procesarse en Gemini
      const res = await api.post(
        `/opportunities/${id}/transcribe-note`,
        { audio_base64: audio.base64, mime_type: audio.mimeType },
        { timeout: 180000 },
      );
      if (res.data.success) {
        const note = res.data.data.text;
        setDescription((prev) => (prev ? `${prev}\n\n${note}` : note));
        toast.success('Nota transcrita — revise e salve');
      }
    } catch (e) {
      console.error('Transcrição falhou:', e);
      toast.error(e.response?.data?.message || 'Falha ao transcrever a nota');
    } finally {
      setTranscribing(false);
    }
  };

  const handleSaveDescription = async () => {
    try {
      setSavingDesc(true);
      const res = await api.put(`/opportunities/${id}`, { description });
      if (res.data.success) toast.success('Descrição salva');
    } catch (e) {
      console.error('Erro ao salvar descrição:', e);
      toast.error('Erro ao salvar a descrição');
    } finally {
      setSavingDesc(false);
    }
  };

  const openActivity = (type) => setActivityModal({ open: true, type });

  // Despacho das ações do playbook: os tipos de actuator executam de verdade
  // (e-mail com rascunho IA, reunião no Calendar); sem contato associado,
  // degradam para o registro manual de atividade.
  const handlePlaybookAction = (type) => {
    if (type === 'send_email') {
      if (contact) setShowEmailModal(true);
      else {
        toast.error('Sem contato associado — registrando atividade manual');
        openActivity('email');
      }
    } else if (type === 'schedule_meeting') {
      if (contact) setShowMeetingModal(true);
      else {
        toast.error('Sem contato associado — registrando atividade manual');
        openActivity('meeting');
      }
    } else {
      openActivity(type);
    }
  };

  const refreshAfterAction = () => {
    fetchAll();
    queryClient.invalidateQueries(['opp-timeline', id]);
  };

  const whatsapp = () => {
    const phone = (contact?.phone || '').replace(/\D/g, '');
    if (!phone) { toast.error('Telefone não disponível'); return; }
    window.open(`https://wa.me/${phone}`, '_blank');
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-4">Erro</h1>
        <p className="text-gray-600 mb-4">{error}</p>
        <button onClick={() => navigate('/pipeline')} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
          Voltar ao Pipeline
        </button>
      </div>
    );
  }

  const stageKey = (opportunity.stage || '').toString().trim().toLowerCase();

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/pipeline')} className="p-2 text-gray-500 hover:text-gray-800" title="Voltar">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{opportunity.title}</h1>
            <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">
              {STAGE_LABELS[stageKey] || opportunity.stage}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!['closed_won', 'closed_lost'].includes(stageKey) && (
            <>
              <button onClick={() => setCloseOutcome('won')} className="flex items-center gap-1 bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 text-sm">
                Ganha
              </button>
              <button onClick={() => setCloseOutcome('lost')} className="flex items-center gap-1 bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 text-sm">
                Perdida
              </button>
            </>
          )}
          <button onClick={() => setShowEdit(true)} className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
            <PencilIcon className="h-4 w-4" /> Editar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          {/* ① Resumen */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Resumo da Oportunidade</h2>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div><dt className="text-gray-500">Valor</dt><dd className="font-semibold text-green-600">{formatCurrency(opportunity.value)}</dd></div>
              <div><dt className="text-gray-500">Probabilidade</dt><dd>{opportunity.probability ?? 0}%</dd></div>
              <div><dt className="text-gray-500">Fonte</dt><dd>{opportunity.source || '—'}</dd></div>
              <div><dt className="text-gray-500">Fechamento esperado</dt><dd>{formatDate(opportunity.expected_close_date)}</dd></div>
              <div><dt className="text-gray-500">Responsável</dt><dd>{opportunity.user?.name || '—'}</dd></div>
              <div><dt className="text-gray-500">Criada em</dt><dd>{formatDate(opportunity.created_at)}</dd></div>
            </dl>
          </section>
          {/* ② Contexto del lead */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contexto do Lead</h2>
            {contact ? (
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div><dt className="text-gray-500">Fonte</dt><dd>{contact.lead_source || opportunity.source || '—'}</dd></div>
                <div><dt className="text-gray-500">Status</dt><dd>{contact.lead_status || '—'}</dd></div>
                <div><dt className="text-gray-500">Etapa (lifecycle)</dt><dd>{contact.lifecycle_stage || '—'}</dd></div>
                <div><dt className="text-gray-500">Último contato</dt><dd>{formatDate(contact.last_contact_date)}</dd></div>
              </dl>
            ) : (
              <p className="text-sm text-gray-500">Sem contato associado a esta oportunidade.</p>
            )}
          </section>

          {/* ③ Enriquecimento IA */}
          {contact && <AIEnrichmentPanel contact={contact} />}

          {/* ④ Histórico da negociação (timeline unificado) */}
          <OpportunityTimeline opportunityId={id} />

          {/* ⑤ Descrição com voz->texto */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Descrição</h2>
              <button
                onClick={handleMicClick}
                disabled={transcribing}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-white ${recorder.recording ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-800'} disabled:opacity-50`}
                title="Ditar nota por voz"
              >
                {recorder.recording ? <StopIcon className="h-4 w-4" /> : <MicrophoneIcon className="h-4 w-4" />}
                {transcribing ? 'Transcrevendo…' : recorder.recording ? 'Parar' : 'Ditar'}
              </button>
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              className="w-full border border-gray-300 rounded-md p-3 text-sm"
              placeholder="Descreva a oportunidade ou dite uma nota com o microfone…"
            />
            <div className="flex justify-end mt-2">
              <button onClick={handleSaveDescription} disabled={savingDesc} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                {savingDesc ? 'Salvando…' : 'Salvar descrição'}
              </button>
            </div>
          </section>
        </div>
        <div className="space-y-6">
          {/* NBA — Próxima melhor ação (IA) */}
          <NbaCard opportunityId={id} />

          {/* Playbook da etapa — o que fazer agora nesta fase do funil */}
          {(() => {
            const playbook = getStagePlaybook(stageKey);
            if (!playbook) return null;
            return (
              <section className="bg-amber-50 border border-amber-200 rounded-lg shadow-sm p-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">
                  Playbook — {STAGE_LABELS[stageKey] || opportunity.stage}
                </h2>
                <p className="text-sm text-amber-800 mb-3">{playbook.hint}</p>
                <ul className="space-y-2">
                  {playbook.actions.map((action) => (
                    <li key={action.label}>
                      {action.type ? (
                        <button
                          onClick={() => handlePlaybookAction(action.type)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-800 bg-white border border-amber-200 hover:bg-amber-100 rounded-md"
                        >
                          {action.label} →
                        </button>
                      ) : (
                        <span className="block px-3 py-2 text-sm text-gray-700">
                          • {action.label}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })()}

          {/* Reativação WhatsApp (cockpit) */}
          <WhatsappCockpit opportunityId={id} />

          {/* ⑥ Ações de contato */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Ações</h2>
            <div className="space-y-2">
              <button onClick={() => openActivity('email')} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"><EnvelopeIcon className="h-4 w-4" /> Registrar E-mail</button>
              <button onClick={() => openActivity('call')} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"><PhoneIcon className="h-4 w-4" /> Registrar Ligação</button>
              <button onClick={() => openActivity('meeting')} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"><CalendarIcon className="h-4 w-4" /> Agendar Reunião</button>
              {contact?.phone && (
                <button onClick={whatsapp} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-2"><DevicePhoneMobileIcon className="h-4 w-4" /> WhatsApp</button>
              )}
            </div>
          </section>

          {/* ⑧ Próximo passo */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Próximo passo</h2>
            <button onClick={() => openActivity('task')} className="w-full bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-700 flex items-center justify-center gap-2 text-sm"><PlusIcon className="h-4 w-4" /> Criar tarefa</button>
          </section>

          {/* ⑦ Contato / Empresa */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Contato / Empresa</h2>
            {contact ? (
              <div className="text-sm space-y-1">
                <Link to={`/contacts/${contact.id}`} className="text-blue-600 hover:underline font-medium">
                  {contact.first_name} {contact.last_name || ''}
                </Link>
                {contact.email && <p className="text-gray-600">{contact.email}</p>}
                {contact.phone && <p className="text-gray-600">{contact.phone}</p>}
              </div>
            ) : (
              <p className="text-sm text-gray-500">—</p>
            )}
            {opportunity.company?.name && (
              <p className="text-sm text-gray-700 mt-2">{opportunity.company.name}</p>
            )}
          </section>
        </div>
      </div>

      {activityModal.open && (
        <ActivityForm
          isOpen={activityModal.open}
          onClose={() => setActivityModal({ open: false, type: null })}
          initialType={activityModal.type}
          contactId={opportunity.contact_id || null}
          companyId={opportunity.company_id || null}
          opportunityId={id}
          onSuccess={() => { setActivityModal({ open: false, type: null }); fetchAll(); queryClient.invalidateQueries(['opp-timeline', id]); }}
        />
      )}

      <OpportunityForm
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        opportunity={opportunity}
        onSuccess={() => { setShowEdit(false); fetchAll(); }}
      />

      <CloseOpportunityModal
        opportunity={opportunity}
        outcome={closeOutcome}
        isOpen={!!closeOutcome}
        onClose={() => setCloseOutcome(null)}
        onSuccess={() => { setCloseOutcome(null); fetchAll(); queryClient.invalidateQueries(['opp-timeline', id]); }}
      />

      {/* Actuadores reais disparados pelo playbook */}
      <EmailComposeModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        contact={contact}
        onSendSuccess={refreshAfterAction}
      />
      <ScheduleMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        contact={contact}
        onScheduleSuccess={refreshAfterAction}
      />
    </div>
  );
};

export default OpportunityDetailPage;
